import React from 'react';
import { Modal, Button, Card, Badge, Row, Col } from 'react-bootstrap';
import { formatDateToYYYYMMDD } from '../../../../utils/dateUtils';
import type { CronJobDetailModalProps } from './types';
import { getCronJobStatusColor, getCronJobStatusText } from './types';

// Helper function to determine if a string is ISO datetime format
const isISOString = (str: string): boolean => {
  const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{3})?)?Z?$/;
  return isoRegex.test(str);
};

// Helper function to format ISO string to readable date/time
const formatISOToDateTime = (isoString: string): string => {
  const date = new Date(isoString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const CronJobDetailModal: React.FC<CronJobDetailModalProps> = ({ show, cronJob, onHide, onEdit }) => {
  if (!cronJob) {
    return null;
  }

  const getMethodColor = (method: string) => {
    switch (method.toUpperCase()) {
      case 'GET':
        return 'primary';
      case 'POST':
        return 'success';
      case 'PUT':
        return 'warning';
      case 'DELETE':
        return 'danger';
      case 'PATCH':
        return 'info';
      default:
        return 'secondary';
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>CronJob Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <h3>{cronJob.name}</h3>
            <Badge bg={getCronJobStatusColor(cronJob.status)} className="fs-6">
              {getCronJobStatusText(cronJob.status)}
            </Badge>
          </div>

          <Row className="mb-4">
            <Col md={6}>
              <Card className="h-100">
                <Card.Header>
                  <h6 className="mb-0">Schedule Information</h6>
                </Card.Header>
                <Card.Body>
                  {isISOString(cronJob.cronExpression) ? (
                    <div>
                      <div className="mb-2">
                        <strong>Type:</strong> <Badge bg="info">One Time</Badge>
                      </div>
                      <div className="mb-2">
                        <strong>Scheduled Time:</strong> {formatISOToDateTime(cronJob.cronExpression)}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="mb-2">
                        <strong>Type:</strong> <Badge bg="secondary">Recurring</Badge>
                      </div>
                      <div className="mb-2">
                        <strong>Cron Expression:</strong> <code>{cronJob.cronExpression}</code>
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="h-100">
                <Card.Header>
                  <h6 className="mb-0">System Information</h6>
                </Card.Header>
                <Card.Body>
                  <div className="mb-2">
                    <strong>CronJob ID:</strong> {cronJob.id}
                  </div>
                  <div className="mb-2">
                    <strong>Created:</strong> {cronJob.createdAt ? formatDateToYYYYMMDD(cronJob.createdAt) : '-'}
                  </div>
                  <div>
                    <strong>Last Updated:</strong> {cronJob.updatedAt ? formatDateToYYYYMMDD(cronJob.updatedAt) : '-'}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="mb-3">
            <Card.Header>
              <h6 className="mb-0">HTTP Command Configuration</h6>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <strong>HTTP Method:</strong>
                <Badge bg={getMethodColor(cronJob.command.argument.method)} className="ms-2">
                  {cronJob.command.argument.method}
                </Badge>
              </div>

              {(cronJob.command.argument as any)?.url && (
                <div className="mb-3">
                  <strong>URL:</strong>
                  <div className="mt-1">
                    <code>{(cronJob.command.argument as any).url}</code>
                  </div>
                </div>
              )}

              {cronJob.command.header && Object.keys(cronJob.command.header).length > 0 && (
                <div className="mb-3">
                  <strong>Headers:</strong>
                  <div className="mt-1">
                    <pre className="bg-light p-2 rounded">{JSON.stringify(cronJob.command.header, null, 2)}</pre>
                  </div>
                </div>
              )}

              {cronJob.command.argument.body && (
                <div className="mb-3">
                  <strong>Request Body:</strong>
                  <div className="mt-1">
                    <pre className="bg-light p-2 rounded">
                      {typeof cronJob.command.argument.body === 'string'
                        ? cronJob.command.argument.body
                        : JSON.stringify(cronJob.command.argument.body, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <h6 className="mb-0">Status Information</h6>
            </Card.Header>
            <Card.Body>
              <div className="d-flex align-items-center">
                <Badge bg={getCronJobStatusColor(cronJob.status)} className="me-2">
                  {getCronJobStatusText(cronJob.status)}
                </Badge>
                <span className="text-muted">
                  {cronJob.status === 'enabled' &&
                    'This cron job is currently enabled and will run according to its schedule'}
                  {cronJob.status === 'disabled' && 'This cron job is currently disabled and will not run'}
                </span>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button variant="primary" onClick={onEdit}>
          Edit CronJob
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CronJobDetailModal;
